import { useEffect, useState } from 'react'
import { getEntries } from '../../../../../shared/audit/audit-api'
import { actorView } from '../../../../../shared/audit/entry-format'

// The course report timeline's "topic/analytic changed" markers (audit
// phase 6, Task 19). A course's analytics setup is audited (edxr-cli
// 20261004171842), so the markers are the setup records' History entries,
// read through content's audit entries API exactly as the record's History
// reads them: organization-scoped, a staff member as EducationXR support,
// and one marker per entry the record's History lists. A reader who may not
// read that History (403/404) gets the periods and no markers.

export const PAGE_SIZE = 200
export const MAX_PAGES = 10

const METHODS = {
  'change.created': 'POST',
  'change.updated': 'PUT',
  'change.restored': 'PUT',
  'change.recycled': 'DELETE',
  'change.deleted': 'DELETE'
}

// toMarker: the shape the timeline's marker items (vis-graph/events.js)
// read; id is the marker's position in its list.
export const toMarker = (entry, i) => ({
  id: i + 1,
  occurredAt: entry.occurredAt,
  method: METHODS[entry.action] || 'PUT',
  name: (entry.record && (entry.record.title || entry.record.label)) || '',
  description: entry.sentence,
  by: actorView(entry.actor).text
})

// splitMarkers: topic and analytic markers, oldest first, each entry once
// (the course timeline reads each topic with its analytics).
export const splitMarkers = entries => {
  const seen = new Set()
  const unique = entries.filter(e => !seen.has(e.id) && seen.add(e.id))
  unique.sort((a, b) => (a.occurredAt < b.occurredAt ? -1 : a.occurredAt > b.occurredAt ? 1 : 0))
  const of = type => unique.filter(e => e.record && e.record.type === type).map(toMarker)
  return { topics: of('analytic_topics'), analytics: of('analytics') }
}

// fetchAllEntries reads every page of one query (at most MAX_PAGES).
export const fetchAllEntries = async params => {
  const items = []
  let before
  for (let page = 0; page < MAX_PAGES; page++) {
    const body = await getEntries(before ? { ...params, limit: PAGE_SIZE, before } : { ...params, limit: PAGE_SIZE })
    items.push(...((body && body.items) || []))
    if (!body || !body.next) break
    before = body.next
  }
  return items
}

const NONE = { topics: [], analytics: [] }

// useTrackingMarkers: the markers of these queries (one per record).
export const useTrackingMarkers = queries => {
  const [markers, setMarkers] = useState(NONE)
  const key = JSON.stringify(queries)
  useEffect(
    () => {
      let live = true
      const list = JSON.parse(key)
      if (!list.length) {
        setMarkers(NONE)
        return undefined
      }
      Promise.all(list.map(q => fetchAllEntries(q).catch(() => []))).then(lists => live && setMarkers(splitMarkers(lists.flat())))
      return () => {
        live = false
      }
    },
    [key]
  )
  return markers
}
